import {
  Box,
  Button,
  Flex,
  IconButton,
  Image,
  Text,
  useDisclosure,
  Skeleton,
  Fade,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { API, graphqlOperation, Storage, Auth } from "aws-amplify";
import { updateBooking } from "../../graphql/mutations";
import { getRoom, getGuest } from "../../graphql/queries";
import { MdNavigateNext, MdNavigateBefore } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { CiboModal } from "../Mini/ciboModal";
import { useSelector } from "react-redux";
import {
  renderCheckInTemplate,
  buildCheckInContext,
} from "../../Helper/checkInTemplate";
import { isPropertyOnly } from "../../Helper/propertyLink";

const resolveImageUrl = async (image) => {
  if (!image || image.startsWith("http")) {
    return image;
  }

  return Storage.get(image);
};

export const Steps = () => {
  const location = useLocation();
  const { room, booking, type, accessTimes, manager: locationManager } =
    location.state;
  const [steps, setSteps] = useState([]);
  const [link, setLink] = useState("");
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const navigate = useNavigate();
  const [roomDB, setRoomDb] = useState({});
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [stepImageUrls, setStepImageUrls] = useState({});
  // Image keys that failed to resolve or load. Tracked per key so a
  // broken image collapses the media pane the same way a missing one
  // does, instead of leaving an empty 300px placeholder behind.
  const [failedImageKeys, setFailedImageKeys] = useState({});
  // Merge-field context. Host-authored {{placeholder}} tokens in
  // step descriptions get substituted against this before any link
  // / iframe parsing, so the guest never sees a raw `{{code}}`.
  // We fetch the guest record by `booking.guestID` because /steps
  // is reached from CheckInComponent which doesn't currently pass
  // `guest` in route state.
  const [guest, setGuest] = useState(null);
  
  // Manager data: prefer the snapshot the caller passed in via
  // location.state (the canonical, name+images-complete shape that
  // /main-v2 itself displays), and only fall back to Redux when the
  // user landed on /steps without it. The Redux `managerName` /
  // `managerImages` fields can be undefined here because some
  // dispatch paths in useLandingV2Logic call `setManagerInfo` with a
  // partial payload, which the reducer applies as an unconditional
  // overwrite — passing the snapshot through the round-trip avoids
  // depending on that Redux state being intact.
  const reduxManager = useSelector((state) => ({
    email: state.user.email,
    password: state.user.password,
    stripeId: state.user.stripeId,
    name: state.user.managerName,
    images: state.user.managerImages,
  }));
  const manager = locationManager || reduxManager;
  
  const {
    isOpen: isOpenCIBO,
    onOpen: onOpenCIBO,
    onClose: onCloseCIBO,
  } = useDisclosure();

  useEffect(() => {
    getRoomItem(room.id);
  }, []);

  // Fetch guest for merge-field context. The check-in screen doesn't
  // need the guest record for anything else, so this stays scoped
  // here rather than being lifted into route state. If the fetch
  // fails the renderer just leaves `guest.firstName` /
  // `guest.phoneLast4` placeholders to resolve to their fallback (or
  // empty string), which is the correct degraded behavior.
  useEffect(() => {
    const guestID = booking?.guestID;
    if (!guestID) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await API.graphql(
          graphqlOperation(getGuest, { id: guestID })
        );
        if (!cancelled) {
          setGuest(res?.data?.getGuest || null);
        }
      } catch (err) {
        console.error("Error fetching guest for check-in merge context:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [booking?.guestID]);

  const mergeContext = useMemo(
    () => buildCheckInContext({ booking, guest, room }),
    [booking, guest, room]
  );

  // Wrap each substituted merge value (real value or fallback) in
  // an accent-colored pill so the guest can visually pick out the
  // personalized bits from the surrounding host-authored text. The
  // parent <Text> already renders everything at fontWeight={800},
  // so plain <strong> wasn't visually distinct; the accent purple
  // mirrors the "Early check-in" badge style used in MainV2 for
  // design consistency. Safe to feed into `dangerouslySetInnerHTML`
  // because the renderer only emits these tags around merge-field
  // output, never around arbitrary host-authored characters.
  const mergePillStyle = [
    "color:#B9B7FF",
    "background:rgba(129,126,255,0.18)",
    "border:1px solid rgba(129,126,255,0.45)",
    "padding:1px 8px",
    "border-radius:6px",
    "white-space:nowrap",
  ].join(";");
  const boldWrap = (value) =>
    `<span style="${mergePillStyle}">${value}</span>`;

  // Derived synchronously rather than through an effect: whether a step
  // has an embed decides whether the media pane renders at all, and
  // state set in an effect lags a render behind the step change, which
  // made the pane flicker when moving between an embed step and a
  // text-only one.
  const renderedDescription = useMemo(() => {
    const step = steps[currentStepIndex];
    if (!step) return "";

    // Merge tokens BEFORE the YouTube regex runs so a future
    // placeholder containing a URL (`{{checkInVideo|...}}` etc.)
    // would still be picked up. Today's placeholders never produce
    // URLs, so behavior for existing content is unchanged.
    // The `<strong>` wrap doesn't interfere with URL matching —
    // the YouTube regex matches the URL substring regardless of
    // surrounding HTML tags, and the resulting iframe replaces
    // the visible text anyway.
    const mergedText = renderCheckInTemplate(step.description, mergeContext, {
      wrap: boldWrap,
    }).rendered;

    const youtubeRegex =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/;
    const match = mergedText.match(youtubeRegex);
    if (!match) return "";

    const videoId = match[1]; // Extract the video ID
    const isShort = mergedText.includes("shorts/"); // Check if it's a YouTube short
    const iframeHeight = isShort ? 600 : 315; // Taller height for shorts

    return `<iframe width="100%" height="${iframeHeight}" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex, steps, mergeContext]);

  const handleLobbyAccess = () => {
    // console.log(room);
    onOpenCIBO(); // Open the modal for lobby access
  };

  const getRoomItem = async (id) => {
    try {
      const roomData = await API.graphql(graphqlOperation(getRoom, { id }));

      if (!roomData.data || !roomData.data.getRoom) {
        throw new Error("Room data not found");
      }

      const room = roomData.data.getRoom;
      setRoomDb(room);

      const user = await Auth.currentUserInfo();
      if (room.ciboLink && user) {
        // const fullName = user.attributes name split
        const ciboLink = JSON.parse(room.ciboLink);

        if (ciboLink.url) {
          const updatedCiboLink = `${ciboLink.url}${booking.reservationID}`;
          setLink(updatedCiboLink);
        }
      }

      if (type === "checkIn" && Array.isArray(room.checkIn)) {
        setSteps(room.checkIn);
      } else if (type === "checkOut" && Array.isArray(room.checkOut)) {
        setSteps(room.checkOut);
      } else if (Array.isArray(room.checkIn)) {
        setSteps(room.checkIn);
      }

    } catch (error) {
      console.error("Error fetching room item:", error);
      // Handle the error appropriately
    }
  };

  const handleNextStep = () => {
    setCurrentStepIndex((prevIndex) => prevIndex + 1);
  };

  const handlePreviousStep = () => {
    setCurrentStepIndex((prevIndex) => prevIndex - 1);
  };

  const currentStep = steps[currentStepIndex];
  const currentImageKey = currentStep?.image || "";
  const currentStepImageUrl = currentImageKey
    ? stepImageUrls[currentImageKey]
    : "";
  const isCurrentImageLoading = Boolean(currentImageKey) && isImageLoading;

  // Steps are allowed to be text-only — hosts can author a check-in
  // step with no image at all. `Skeleton` forwards `minH` to its
  // wrapper even once `isLoaded` is true, so rendering the media pane
  // for an imageless step leaves a 300px empty block on mobile and
  // hands the pane its `flex: 1.2` share of the row on desktop,
  // squeezing the instructions beside dead space. Drop the pane
  // entirely instead.
  const stepsLoaded = steps.length > 0 && Boolean(currentStep);
  const hasEmbed = renderedDescription.includes("iframe");
  const hasStepImage =
    Boolean(currentImageKey) && !failedImageKeys[currentImageKey];
  // Keep the placeholder while steps are still in flight: until the
  // step resolves we don't know whether it has media, and showing the
  // skeleton is a better loading signal than collapsing the layout.
  const showMediaPane = !stepsLoaded || hasEmbed || hasStepImage;

  useEffect(() => {
    setIsImageLoading(Boolean(currentImageKey));
  }, [currentImageKey]);

  useEffect(() => {
    if (!currentImageKey) {
      return;
    }

    if (stepImageUrls[currentImageKey]) {
      return;
    }

    let isCurrent = true;

    resolveImageUrl(currentImageKey)
      .then((url) => {
        if (!isCurrent) return;
        setStepImageUrls((prev) => ({ ...prev, [currentImageKey]: url }));
      })
      .catch((error) => {
        console.error("Error loading step image:", error);
        if (isCurrent) {
          setIsImageLoading(false);
          setFailedImageKeys((prev) =>
            prev[currentImageKey]
              ? prev
              : { ...prev, [currentImageKey]: true }
          );
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [currentImageKey, stepImageUrls]);

  useEffect(() => {
    const nextImageKey = steps[currentStepIndex + 1]?.image;

    if (!nextImageKey || stepImageUrls[nextImageKey]) {
      return;
    }

    let isCurrent = true;

    resolveImageUrl(nextImageKey)
      .then((url) => {
        if (!isCurrent) return;

        setStepImageUrls((prev) => ({ ...prev, [nextImageKey]: url }));

        if (url && typeof window !== "undefined") {
          const preloadImage = new window.Image();
          preloadImage.decoding = "async";
          preloadImage.src = url;
        }
      })
      .catch((error) => {
        console.error("Error preloading next step image:", error);
      });

    return () => {
      isCurrent = false;
    };
  }, [currentStepIndex, stepImageUrls, steps]);

  const handleSelect = async () => {
    try {
      // Define the explicit status value
      const explicitStatus = {
        initial: true,
        document: true,
        card: true,
        deposit: true,
        done: true,
      };

      // Only update booking if the type is 'checkIn'
      let updatedBooking = booking;
      if (type === "checkIn" && !isPropertyOnly(booking)) {
        updatedBooking = { ...booking, status: JSON.stringify(explicitStatus) };

        await API.graphql(
          graphqlOperation(updateBooking, {
            input: { id: booking.id, status: updatedBooking.status },
          })
        );

        console.log("Booking updated successfully:", updatedBooking);
      }

      // Navigate to /main with the booking info (updated or not based on the type)
      const isV2 = sessionStorage.getItem('isV2') === 'true';
      navigate(isV2 ? '/main-v2' : '/main', {
        state: { room, booking: updatedBooking, manager, accessTimes },
      });
    } catch (error) {
      console.error("Error updating booking:", error);
    }
  };

  const parseDescriptionWithLinks = (text) => {
    if (!text) return "";

    const urlRegex =
      /(\b(https?|ftp|file):\/\/[\-A-Z0-9+&@#/%?=~_|!:,.;]*[\-A-Z0-9+&@#/%=~_|])/gi;

    return text.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #817EFF; text-decoration: underline;">${url}</a>`;
    });
  };

  // Merge-rendered description for display + the lock-code keyword
  // check. Token-substituted text is what the guest will actually
  // read, so it's also what we scan for "code"-style keywords. The
  // <strong> wrap inserted around merge values doesn't interrupt
  // the literal "code"/"passcode"/etc. matches because those words
  // come from the host-authored surrounding text, not from the
  // substituted segments.
  const mergedDescription = useMemo(
    () =>
      renderCheckInTemplate(currentStep?.description, mergeContext, {
        wrap: boldWrap,
      }).rendered,
    [currentStep, mergeContext]
  );

  // Show lock code if current step mentions code-related keywords
  const stepText = `${currentStep?.title || ""} ${mergedDescription}`;
  const showLockCode = Boolean(booking?.code) && /(passcode|door\s*lock\s*code|lock\s*code|code)/i.test(stepText);

  return (
    <>
      <Flex
        align="flex-start"
        justify="flex-start"
        width="100%"
        // Text-only steps get a narrower column — a lone instructions
        // card stretched to the full two-pane width reads badly.
        maxW={showMediaPane ? "1100px" : "640px"}
        mx="auto"
        flexDirection={{ base: "column", lg: "row" }}
        gap={{ base: 4, lg: 8 }}
        p={{ base: 2, md: 8 }}
        pt={{ md: 10 }}
      >
        {/* Step media — left pane on desktop, omitted for text-only steps */}
        {showMediaPane && (
          <Box width="100%" flex={{ lg: "1.2" }} minW={0}>
            {!stepsLoaded ? (
              // Loading skeleton for when steps are not yet loaded
              <Skeleton
                borderRadius="5px"
                minH="300px"
                startColor="gray.700"
                endColor="gray.600"
              />
            ) : hasEmbed ? (
              <Box
                dangerouslySetInnerHTML={{
                  __html: renderedDescription,
                }}
              />
            ) : (
              <Skeleton
                isLoaded={!isCurrentImageLoading}
                borderRadius="5px"
                minH="300px"
                startColor="gray.700"
                endColor="gray.600"
              >
                <Image
                  borderRadius="5px"
                  src={currentStepImageUrl || undefined}
                  alt={currentStep.title || "Step image"}
                  objectFit="cover"
                  width="100%"
                  loading={currentStepIndex === 0 ? "eager" : "lazy"}
                  decoding="async"
                  fetchPriority={currentStepIndex === 0 ? "high" : "auto"}
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => {
                    setIsImageLoading(false);
                    setFailedImageKeys((prev) =>
                      prev[currentImageKey]
                        ? prev
                        : { ...prev, [currentImageKey]: true }
                    );
                  }}
                  style={{
                    opacity: isCurrentImageLoading ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out'
                  }}
                />
              </Skeleton>
            )}
          </Box>
        )}

        {/* Instructions — right pane on desktop, full width when alone */}
        <Box
          w="100%"
          flex={{ lg: "1" }}
          minW={0}
          // Sticky only earns its keep next to a taller media pane.
          position={{ lg: showMediaPane ? "sticky" : "static" }}
          top={{ lg: 8 }}
        >
          <Box
            bg={{ base: "#1a202c", md: "rgba(255, 255, 255, 0.03)" }}
            p={{ base: 2, md: 6 }}
            border="1px solid #494949"
            borderRadius={{ base: "5px", md: "16px" }}
          >
            <Skeleton 
              isLoaded={stepsLoaded}
              startColor="gray.700"
              endColor="gray.600"
              mb={2}
            >
              <Text fontSize={"12px"} fontWeight={"bold"} pb={1}>
                {type == "checkIn"
                  ? "Check In instructions"
                  : "Check Out instructions"}
              </Text>
            </Skeleton>

            <Skeleton 
              isLoaded={stepsLoaded}
              startColor="gray.700"
              endColor="gray.600"
              mb={6}
            >
              <Text
                fontSize={"16px"}
                fontWeight={800}
                whiteSpace="pre-wrap"
                dangerouslySetInnerHTML={{
                  __html: parseDescriptionWithLinks(mergedDescription),
                }}
              />
            </Skeleton>

            {showLockCode && (
              <Box mt={4} p={3} border="1px solid #494949" borderRadius="8px" bg="#1a202c">
                <Text fontSize="12px" color="#D0D0D0" fontWeight={600} mb={"6px"}>
                  Smart Lock Code
                </Text>
                <Text fontSize="16px" fontWeight={800}>
                  {booking.code}
                </Text>
              </Box>
            )}

            <Flex pt={6} justifyContent="space-between" alignItems="center">
              <Skeleton 
                isLoaded={steps.length > 0}
                startColor="gray.700"
                endColor="gray.600"
                borderRadius="md"
              >
                <IconButton
                  onClick={handlePreviousStep}
                  color={"white"}
                  fontSize={"14px"}
                  isDisabled={currentStepIndex === 0}
                  icon={<MdNavigateBefore />}
                />
              </Skeleton>

              {steps.length > 0 && (
                <Text fontSize="12px" color="gray.400" fontWeight={600}>
                  Step {currentStepIndex + 1} of {steps.length}
                </Text>
              )}

              <Skeleton 
                isLoaded={steps.length > 0}
                startColor="gray.700"
                endColor="gray.600"
                borderRadius="md"
              >
                <Box>
                  {currentStepIndex === steps.length - 1 ? (
                    <>
                      {roomDB.ciboLink &&
                        parseInt(JSON.parse(roomDB.ciboLink).step, 10) ===
                          currentStepIndex + 1 && (
                        <Button
                          onClick={handleLobbyAccess}
                          fontSize={"14px"}
                          mr={"5px"}
                          bg={"#817efc"}
                          color="white"
                        >
                          Open Lobby
                        </Button>
                      )}
                      <Button
                        onClick={handleSelect}
                        color="white"
                        fontSize="14px"
                        bg="#817efc"
                        _active={{ bg: "#817efc" }}
                        _hover={{ bg: "#817efc" }}
                      >
                        Done
                      </Button>
                    </>
                  ) : (
                    <>
                      {roomDB.ciboLink &&
                        parseInt(JSON.parse(roomDB.ciboLink).step) ===
                          currentStepIndex + 1 && (
                        <Button
                          onClick={handleLobbyAccess}
                          fontSize={"14px"}
                          mr={"5px"}
                          bg={"#817efc"}
                          _active={{ bg: "#817efc" }}
                          _hover={{ bg: "#817efc" }}
                          color="white"
                        >
                          Open Lobby
                        </Button>
                      )}
                      <Button
                        onClick={handleNextStep}
                        isDisabled={currentStepIndex === steps.length - 1}
                        rightIcon={<MdNavigateNext />}
                        color={"white"}
                        fontSize={"14px"}
                      >
                        Next
                      </Button>
                    </>
                  )}
                </Box>
              </Skeleton>
            </Flex>
          </Box>
        </Box>
      </Flex>
      {room.ciboLink && (
        <CiboModal onClose={onCloseCIBO} isOpen={isOpenCIBO} link={link} />
      )}
    </>
  );
};
